/**
 * client/input/pad.ts — Gamepad API normalisation: device pick, standard vs raw DualSense mapping
 * (pure: works on a structural GamepadLike so it runs in Node tests).
 *
 * OWNER: INPUT agent.
 * Sources: research/controller.md §1 (standard indices, touchpad = 17), §8.1 (per-browser matrix,
 * non-standard raw HID order fallback from Firefox DualSenseRemapper / Chromium MapperDualSense).
 */
import { PAD_BUTTON_COUNT, Pad } from './layouts';

/** Structural subset of the DOM Gamepad we read (tests pass plain objects). */
export interface GamepadLike {
  readonly id: string;
  readonly index: number;
  readonly connected: boolean;
  readonly mapping: string;
  readonly timestamp: number;
  readonly axes: readonly number[];
  readonly buttons: readonly { readonly pressed: boolean; readonly value: number }[];
}

/** One normalised sample. Stick axes are browser convention (+x right, +y DOWN), raw (no deadzone). */
export interface PadSample {
  connected: boolean;
  index: number;
  id: string;
  isDualSense: boolean;
  /** 'standard' or 'dualsense-raw' (non-standard fallback) or 'generic'. */
  mapping: string;
  /** Raw button values 0..1 (triggers analog; others 0/1), indexed by Pad. */
  buttons: number[];
  lx: number; ly: number; rx: number; ry: number;
  timestamp: number;
}

export function createPadSample(): PadSample {
  return {
    connected: false, index: -1, id: '', isDualSense: false, mapping: '', buttons: new Array(PAD_BUTTON_COUNT).fill(0),
    lx: 0, ly: 0, rx: 0, ry: 0, timestamp: 0,
  };
}

/** Sony vendor 054c, DualSense 0ce6 / DualSense Edge 0df2 [CTL §8.1]. */
export function isDualSenseId(id: string): boolean {
  const s = id.toLowerCase();
  return s.includes('dualsense') || (s.includes('054c') && (s.includes('0ce6') || s.includes('0df2')));
}
export function isSonyId(id: string): boolean {
  const s = id.toLowerCase();
  return s.includes('054c') || s.includes('dualsense') || s.includes('dualshock') || s.includes('wireless controller');
}

/**
 * Per-pad state the raw fallback needs: Chrome/Firefox report a never-moved trigger axis as 0,
 * which would read as half-pressed on the -1..1 scale. Treat a trigger axis as released until it
 * has reported a value other than exactly 0.
 */
export interface PadRawState { trigSeen: [boolean, boolean] }
export function createPadRawState(): PadRawState { return { trigSeen: [false, false] }; }

function btn(gp: GamepadLike, i: number): number {
  const b = gp.buttons[i];
  if (!b) return 0;
  return typeof b.value === 'number' && b.value > 0 ? b.value : b.pressed ? 1 : 0;
}

/** Hat switch (axis 9 in the raw DualSense order): -1 up ... clockwise in 2/7 steps; >1 neutral. */
function hatToDpad(v: number, out: number[]): void {
  out[Pad.DPAD_UP] = 0; out[Pad.DPAD_DOWN] = 0; out[Pad.DPAD_LEFT] = 0; out[Pad.DPAD_RIGHT] = 0;
  if (!(v >= -1.01 && v <= 1.01)) return;
  const dir = Math.round(((v + 1) * 7) / 2); // 0 up, 1 up-right, 2 right, ... 7 up-left
  if (dir === 7 || dir === 0 || dir === 1) out[Pad.DPAD_UP] = 1;
  if (dir >= 1 && dir <= 3) out[Pad.DPAD_RIGHT] = 1;
  if (dir >= 3 && dir <= 5) out[Pad.DPAD_DOWN] = 1;
  if (dir >= 5 && dir <= 7) out[Pad.DPAD_LEFT] = 1;
}

/** Normalise one browser Gamepad into `out`. */
export function readPad(gp: GamepadLike, out: PadSample, raw: PadRawState): PadSample {
  out.connected = gp.connected;
  out.index = gp.index;
  out.id = gp.id;
  out.isDualSense = isDualSenseId(gp.id);
  out.timestamp = gp.timestamp;
  const b = out.buttons;
  if (gp.mapping === 'standard' || !isSonyId(gp.id)) {
    out.mapping = gp.mapping === 'standard' ? 'standard' : 'generic';
    for (let i = 0; i < PAD_BUTTON_COUNT; i++) b[i] = btn(gp, i);
    out.lx = gp.axes[0] ?? 0; out.ly = gp.axes[1] ?? 0; out.rx = gp.axes[2] ?? 0; out.ry = gp.axes[3] ?? 0;
    return out;
  }
  // Non-standard Sony pad: raw HID order [CTL §8.1]
  //   buttons: 0 □, 1 ✕, 2 ○, 3 △, 4 L1, 5 R1, 6 L2, 7 R2, 8 Create, 9 Options, 10 L3, 11 R3, 12 PS, 13 Touchpad
  //   axes: 0 LX, 1 LY, 2 RX, 3 L2 (-1..1), 4 R2 (-1..1), 5 RY, 9 hat
  out.mapping = 'dualsense-raw';
  b[Pad.SQUARE] = btn(gp, 0); b[Pad.CROSS] = btn(gp, 1); b[Pad.CIRCLE] = btn(gp, 2); b[Pad.TRIANGLE] = btn(gp, 3);
  b[Pad.L1] = btn(gp, 4); b[Pad.R1] = btn(gp, 5);
  b[Pad.CREATE] = btn(gp, 8); b[Pad.OPTIONS] = btn(gp, 9); b[Pad.L3] = btn(gp, 10); b[Pad.R3] = btn(gp, 11);
  b[Pad.PS] = btn(gp, 12); b[Pad.TOUCHPAD] = btn(gp, 13);
  const a3 = gp.axes[3], a4 = gp.axes[4];
  if (a3 !== undefined && a3 !== 0) raw.trigSeen[0] = true;
  if (a4 !== undefined && a4 !== 0) raw.trigSeen[1] = true;
  const l2 = raw.trigSeen[0] && a3 !== undefined ? (a3 + 1) / 2 : 0;
  const r2 = raw.trigSeen[1] && a4 !== undefined ? (a4 + 1) / 2 : 0;
  // the digital L2/R2 buttons (6/7) back up the analog axes
  b[Pad.L2] = Math.max(l2, btn(gp, 6) > 0.5 ? 1 : 0);
  b[Pad.R2] = Math.max(r2, btn(gp, 7) > 0.5 ? 1 : 0);
  if (gp.axes.length > 9) hatToDpad(gp.axes[9], b);
  else { b[Pad.DPAD_UP] = btn(gp, 14); b[Pad.DPAD_DOWN] = btn(gp, 15); b[Pad.DPAD_LEFT] = btn(gp, 16); b[Pad.DPAD_RIGHT] = btn(gp, 17); }
  out.lx = gp.axes[0] ?? 0; out.ly = gp.axes[1] ?? 0; out.rx = gp.axes[2] ?? 0; out.ry = gp.axes[5] ?? 0;
  return out;
}

/** Any button pressed or a stick past the deadzone (for "which pad is the player using"). */
export function padHasInput(s: PadSample): boolean {
  for (let i = 0; i < PAD_BUTTON_COUNT; i++) if (s.buttons[i] > 0.5) return true;
  return Math.hypot(s.lx, s.ly) > 0.5 || Math.hypot(s.rx, s.ry) > 0.5;
}

/**
 * Device pick [CTL §8.2]: keep the active pad while it stays connected; switch to another pad when it
 * produces input; with no active pad prefer a DualSense, then any "standard" pad, then anything.
 */
export function pickPad(pads: readonly (GamepadLike | null)[], activeIndex: number, hasInput: (gp: GamepadLike) => boolean): number {
  let fallback = -1, fallbackScore = -1;
  for (const gp of pads) {
    if (!gp || !gp.connected) continue;
    if (gp.index !== activeIndex && hasInput(gp)) return gp.index;
    const score = (isDualSenseId(gp.id) ? 4 : isSonyId(gp.id) ? 2 : 0) + (gp.mapping === 'standard' ? 1 : 0);
    if (score > fallbackScore) { fallbackScore = score; fallback = gp.index; }
  }
  for (const gp of pads) if (gp && gp.connected && gp.index === activeIndex) return activeIndex;
  return fallback;
}
